import React, { FC, memo, useState, useRef, useCallback } from 'react';

import { ItemReference } from '../../../../stores/dataStore';
import EquipmentTypeEditor from '../../../../widgets/bussiness/equipment-type-editor';
import OemEditor from '../../../../widgets/bussiness/oem-editor';
import MachineModelEditor from '../../../../widgets/bussiness/machine-model-editor';
import MRClusterEditor from '../../../../widgets/bussiness/mrcluster-editor';
import { Icon } from 'semantic-ui-react';
import { PatternRowDto } from 'stores/configuration/machinery/pattern-rows-store';
import MultiMachineUnitEditor from 'widgets/bussiness/multi-machine-unit-editor';
import MultiPlcTypeEditor from 'widgets/bussiness/multi-plc-type-editor';

export interface MachineListItemViewModel {
  oem: ItemReference;
  equipmentType: ItemReference;
  machineModel: ItemReference;
  cluster: ItemReference;
  machineUnits: string[];
  plcTypes: string[];
  machineUnitItems?: ItemReference[];
  plcTypeItems?: ItemReference[];
  hasAvailablePlcTypes?: boolean;
}

interface MachineListItemProps {
  onEditMode?: boolean;
  canDelete?: boolean;
  machine: MachineListItemViewModel;
  pattern?: PatternRowDto;
  automaticallyAddedMachines?: boolean;

  onChange?: (machine: MachineListItemViewModel) => void;
  onDeleteClose?: () => void;
}

const MachineListItem: FC<MachineListItemProps> = ({
  onEditMode,
  canDelete,
  pattern,
  machine,
  automaticallyAddedMachines,
  onChange,
  onDeleteClose
}) => {
  const { cluster, equipmentType, oem, machineModel, machineUnits, plcTypes, machineUnitItems, plcTypeItems } = machine;
  const [relatedClusterIds, setRelatedClusterIds] = useState([]);
  const [relatedEqIds, setRelatedEqIds] = useState([]);

  // Local state for hasAvailablePlcTypes (do not propagate to parent)
  //const [hasAvailablePlcTypes, setHasAvailablePlcTypes] = useState(machine.hasAvailablePlcTypes ?? false);

  const lastMachineRef = useRef<string>('');

  const handleOnChange = useCallback(
    (property: keyof MachineListItemViewModel, value: any) => {
      const newMachine = { ...machine };
      newMachine[property] = value;

      if (property === 'cluster' && value?.id !== cluster?.id) {
        newMachine.equipmentType = null;
        newMachine.oem = oem ? (value == null ? null : oem) : null;
        newMachine.machineModel = null;
        newMachine.machineUnits = null;
        newMachine.plcTypes = null;
      }
      if (property === 'equipmentType' && value?.id !== equipmentType?.id) {
        setRelatedClusterIds([]);
        setRelatedEqIds([]);
        // Only update cluster if the new equipmentType has a different one
        if (value?.cluster && (!cluster || value.cluster.id !== cluster.id)) {
          newMachine.cluster = value.cluster;
        }
        newMachine.oem = oem ? (value == null ? null : oem) : null;
        newMachine.machineModel = null;
        newMachine.machineUnits = null;
        newMachine.plcTypes = null;
      }
      if (property === 'oem' && value?.id !== oem?.id) {
        if (!automaticallyAddedMachines) {
          setRelatedClusterIds(value?.relatedClusterIds || []);
          setRelatedEqIds(value?.relatedEqIds || []);
          if (value == null || newMachine.cluster == null || newMachine.equipmentType == null) {
            if (value?.cluster && (!cluster || value.cluster.id !== cluster.id)) {
              newMachine.cluster = value.cluster;
            }
            if (value?.equipmentType && (!equipmentType || value.equipmentType.id !== equipmentType.id)) {
              newMachine.equipmentType = value.equipmentType;
            }
          }
        }
        newMachine.machineUnits = null;
        newMachine.machineModel = null;
        newMachine.plcTypes = null;
      }
      if (property === 'machineModel' && value?.id !== machineModel?.id) {
        setRelatedClusterIds([]);
        setRelatedEqIds([]);
        if (value?.cluster && (!cluster || value.cluster.id !== cluster.id)) {
          newMachine.cluster = value.cluster;
        }
        if (value?.equipmentType && (!equipmentType || value.equipmentType.id !== equipmentType.id)) {
          newMachine.equipmentType = value.equipmentType;
        }
        if (value?.oem && (!oem || value.oem.id !== oem.id)) {
          newMachine.oem = value.oem;
        }
        newMachine.plcTypes = null;
        newMachine.machineUnits = null;
      }

      // Verify it actually changed before calling onChange
      const machineKey = JSON.stringify(newMachine);
      if (lastMachineRef.current !== machineKey) {
        lastMachineRef.current = machineKey;
        onChange?.(newMachine);
      }
    },
    [machine, cluster, equipmentType, oem, machineModel, automaticallyAddedMachines, onChange]
  );

  // handleOnPlcTypesLoaded updates local state and propagates to parent
  const handleOnPlcTypesLoaded = useCallback(
    (items: any[]): void => {
      const hasAvailable = items.length > 0;
      // Update local state
      //setHasAvailablePlcTypes(hasAvailable);

      // Propagate to parent using handleOnChange which has the correct deduplication logic
      handleOnChange('hasAvailablePlcTypes', hasAvailable);
    },
    [handleOnChange]
  );

  const clusterBlocked = pattern && !!pattern.machineRelatedClusterId;
  const equipmentTypeBlocked = pattern && !!pattern.equipmentTypeId;
  const oemBlocked = pattern && !!pattern.oemId;
  const machineModelBlocked = pattern && !!pattern.machineModelId;

  return (
    <>
      <div className="border-bottom-gray">
        <MRClusterEditor
          nullable
          reloadOnChange
          className={onEditMode ? 'form__box-content' : 'mr-table__dropdown'}
          readOnly={!onEditMode || clusterBlocked || (cluster != null && automaticallyAddedMachines)}
          value={cluster || null}
          onChange={value => handleOnChange('cluster', value)}
          oemId={oem?.id || null}
          relatedClusterIds={relatedClusterIds || []}
          disabled={cluster != null && automaticallyAddedMachines}
        />
      </div>

      <div className="border-bottom-gray">
        <EquipmentTypeEditor
          nullable
          clearOnReload
          reloadOnChange
          className={onEditMode ? 'form__box-content' : 'mr-table__dropdown'}
          readOnly={!onEditMode || equipmentTypeBlocked || (equipmentType != null && automaticallyAddedMachines)}
          clusterId={cluster?.id || null}
          value={equipmentType}
          onChange={eqType => handleOnChange('equipmentType', eqType)}
          oemId={oem?.id || null}
          relatedEquipmentTypeIds={relatedEqIds || []}
          disabled={equipmentType != null && automaticallyAddedMachines}
        />
      </div>

      <div className="border-bottom-gray">
        <OemEditor
          nullable
          clearOnReload
          reloadOnChange
          className={onEditMode ? 'form__box-content' : 'mr-table__dropdown'}
          readOnly={!onEditMode || oemBlocked}
          value={oem}
          onChange={oem => handleOnChange('oem', oem)}
          equipmentId={equipmentType?.id || null}
          clusterId={cluster?.id || null}
        />
      </div>

      <div className="border-bottom-gray">
        <MachineModelEditor
          nullable
          clearOnReload
          reloadOnChange
          className={onEditMode ? 'form__box-content' : 'mr-table__dropdown'}
          readOnly={!onEditMode || machineModelBlocked}
          value={machineModel}
          onChange={machineModel => handleOnChange('machineModel', machineModel)}
          oemId={oem?.id || null}
          equipmentId={equipmentType?.id || null}
          clusterId={cluster?.id || null}
        />
      </div>

      <div className="border-bottom-gray">
        <MultiMachineUnitEditor
          placeholder={'Select Machine Unit'}
          className={onEditMode ? 'form__box-content' : 'mr-table__dropdown'}
          value={machineUnits}
          readOnly={!onEditMode}
          oemId={oem?.id || null}
          equipmentId={equipmentType?.id || null}
          onChange={mu => handleOnChange('machineUnits', mu)}
          multiple={true}
          machineUnitOptions={machineUnitItems}
        />
      </div>

      <div className="border-bottom-gray">
        <MultiPlcTypeEditor
          key={machineModel?.id || ''}
          placeholder={'Select PLC type'}
          className={onEditMode ? 'form__box-content' : 'mr-table__dropdown'}
          value={plcTypes}
          readOnly={!onEditMode}
          oemId={oem?.id || null}
          equipmentId={equipmentType?.id || null}
          onChange={pt => handleOnChange('plcTypes', pt)}
          multiple={true}
          plcTypeOptions={plcTypeItems}
          onPlcTypesLoaded={handleOnPlcTypesLoaded}
        />
      </div>
      <div className="event-details-tab__mr-table__remove border-bottom-gray">
        {onEditMode && canDelete && <Icon color="red" name="close" link onClick={onDeleteClose} />}
      </div>
    </>
  );
};

// Improved comparison for memo
const areEqual = (prevProps: MachineListItemProps, nextProps: MachineListItemProps) => {
  return (
    prevProps.onEditMode === nextProps.onEditMode &&
    prevProps.canDelete === nextProps.canDelete &&
    prevProps.automaticallyAddedMachines === nextProps.automaticallyAddedMachines &&
    prevProps.pattern?.machineRelatedClusterId === nextProps.pattern?.machineRelatedClusterId &&
    prevProps.machine.cluster?.id === nextProps.machine.cluster?.id &&
    prevProps.machine.equipmentType?.id === nextProps.machine.equipmentType?.id &&
    prevProps.machine.oem?.id === nextProps.machine.oem?.id &&
    prevProps.machine.machineModel?.id === nextProps.machine.machineModel?.id &&
    JSON.stringify(prevProps.machine.machineUnits) === JSON.stringify(nextProps.machine.machineUnits) &&
    JSON.stringify(prevProps.machine.plcTypes) === JSON.stringify(nextProps.machine.plcTypes) &&
    JSON.stringify(prevProps.machine.plcTypeItems) === JSON.stringify(nextProps.machine.plcTypeItems) &&
    JSON.stringify(prevProps.machine.machineUnitItems) === JSON.stringify(nextProps.machine.machineUnitItems)
    // DO NOT compare hasAvailablePlcTypes
  );
};

MachineListItem.defaultProps = { onChange: () => {}, onDeleteClose: () => {} };

export default memo(MachineListItem, areEqual);
